import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { Invitation, NetworkPartner, User } from '@kion/data-models';
import { PrivilegesFacade } from '@kion/data-access/data-access-privileges';
import { UsersFacade } from '@kion/data-access/data-access-users';
import { NetworkPartnersFacade } from '@kion/data-access/data-access-network-partners';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { ToastService } from '@kion/ui';
import { CookieService } from 'ngx-cookie-service';
import { CmsConfig } from '../../config/cms.config';
import { TranslateService } from '@ngx-translate/core';
import { take, takeUntil } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
  selector: 'kion-cms-nwp-admin-invite',
  templateUrl: './nwp-admin-invite.component.html',
  styleUrls: ['./nwp-admin-invite.component.scss']
})
export class NwpAdminInviteComponent implements OnInit, OnDestroy {

  showComponent = 'trusted';

  form: FormGroup;
  selectedRoles = [];
  networkPartners$: Observable<NetworkPartner[]>;
  isSending = false;
  user$: Subject<User> = new Subject<User>();

  roles = [
    {
      id: 'fsAdmin',
      name: 'Admin'
    },
    {
      id: 'fsNwpAdmin',
      name: 'NetworkPartner Admin'
    },
    {
      id: 'fsNwpViewer',
      name: 'NetworkPartner Viewer'
    }
  ];

  private readonly subSink: SubSink;

  constructor(
    formBuilder: FormBuilder,
    private privilegesFacade: PrivilegesFacade,
    private usersFacade: UsersFacade,
    private networkPartnersFacade: NetworkPartnersFacade,
    private correlationObserver: CorrelationObserverService,
    private toastMessageService: ToastService,
    private cookieService: CookieService,
    private cmsConfig: CmsConfig,
    private translationService: TranslateService
  ) {
    this.subSink = new SubSink();
    this.form = formBuilder.group({
      email: new FormControl(),
      firstName: new FormControl(),
      lastName: new FormControl(),
      baseStore: new FormControl(),
    });
    this.networkPartners$ = this.networkPartnersFacade.all$;
  }

  ngOnInit(): void {
    this.usersFacade.loadUserById();
    this.networkPartnersFacade.loadAll(this.cmsConfig.baseSite);
    this.subSink.sink = this.usersFacade.all$.pipe(takeUntil(this.user$)).subscribe(
      users => {
        if (users.length > 0) {
          const user = users[users.length - 1];
          this.user$.next(user);
        }
      }
    );
    this.subSink.sink = this.user$.pipe(take(1)).subscribe(
      user => {
        this.privilegesFacade.loadAll(this.cmsConfig.baseSite, user.uid)
      }
    );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  inviteUser(): void {
    this.isSending = true;
    const invitation: Invitation = {
      emailAddress: this.form.get('email').value,
      firstName: this.form.get('firstName').value,
      invitationRoleMappings: {
        roleMappings: [
          {
            baseStore: 'global',
            role: {
              uid: 'fsAdmin'
            }
          }
        ]
      },
      lastName: this.form.get('lastName').value
    };
    this.subSink.sink = this.correlationObserver.subscribeToAction(
      this.usersFacade.inviteUser(invitation, true),
      () => {
        this.isSending = false;
        this.toastMessageService.show(
          {
            text: this.translationService.instant('admin.toast.invitation.success'),
            type: 'success',
          }
        )
      },
      () => {
        this.isSending = false;
        this.toastMessageService.show(
          {
            text: this.translationService.instant('admin.toast.invitation.error'),
            type: 'error',
          }
        )
      }
    )
  }
}
